<template>
    <div :class="computedColClass">
        <div :class="otherClass">

            <!-- LABEL -->
            <label v-if="title" :for="inputId" :class="labelClass">
                <slot name="title">{{ toTitleCase(title) }}</slot>
                <sup v-if="req" :class="spanClass">*</sup>
            </label>

            <!-- INPUT -->
            <input
                :type="type"
                :id="inputId"
                :name="fieldName"
                :value="modelValue"
                @input="$emit('update:modelValue', $event.target.value)"
                :placeholder="placeholder ? toTitleCase(placeholder) : title?.replaceAll('_', ' ')"
                :disabled="disabled"
                :readonly="readonly"
                :class="computedInputClass"
                :style="validationStyle"
                @blur="$emit('customBlur')"
            />

            <!-- ERROR MESSAGE -->
            <Error
                v-if="hasError"
                :id="errorId"
                :message="errorMessage"
                :bootstrap="bootstrap"
            />
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { useValidationStore } from '@/stores/validationStore.js';

const props = defineProps({
    modelValue: [String, Number],
    title: String,
    inputId: { type: String, default: null },
    otherClass: { type: String, default: 'mb-3' },
    field: String,
    type: { type: String, default: 'text' },
    col: String,
    req: { type: Boolean, default: false },
    customClass: {type: Boolean, default: false},
    placeholder: String,
    disabled: Boolean,
    readonly: Boolean,
    bootstrap: { type: Boolean, default: false }
});

const validationStore = useValidationStore();

// Computed error info
const errorMessage = computed(() => validationStore.getErrors(fieldName.value));
const hasError = computed(() => validationStore.hasError(fieldName.value));

// Computed IDs
const inputId = computed(() => props.inputId ?? fieldName.value);
const errorId = computed(() => `error-${inputId.value}`);

// Field name logic
const fieldName = computed(() => {
    if (!props.field) return "";
    const shortKey = props.field.split(".").pop();
    return validationStore.hasError(props.field) ? props.field
        : validationStore.hasError(shortKey) ? shortKey
            : props.field;
});

// Computed col classes
const computedColClass = computed(() => {
    if (props.col && !props.customClass) return props.col;
    return props.bootstrap
        ? 'col-12 col-md-6 col-lg-4'
        : 'col-span-12 md:col-span-6 lg:col-span-4';
});

// Computed input classes
const computedInputClass = computed(() => {
    if (props.bootstrap) {
        return [
            'form-control',
            hasError.value ? 'is-invalid' : '',
        ].join(' ');
    } else {
        return [
            props.customClass ? props.col : 'block w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1',
            hasError.value ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : (!hasError.value && props.req && props.modelValue ? 'border-green-300 focus:ring-green-300 focus:border-green-300' : 'border-gray-300 focus:ring-green-200 focus:border-green-200')
        ].join(' ');
    }
});

// Label and span classes
const labelClass = computed(() => props.bootstrap ? 'form-label' : 'block text-sm font-semibold text-slate-700 mb-2');
const spanClass = computed(() => props.bootstrap ? 'text-danger' : 'text-red-500');

// Validation style
const validationStyle = computed(() => hasError.value && !props.bootstrap ? "border-color: red !important;" : "");

// Utility function
const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

</script>
